import {SearchBlockGlobalConfig} from 'quickstart/blocks/SearchBlock/admin'
import {useMemo} from 'react'
import {deepMerge, logger, truthy, tryJson} from 'tizra'
import {UseSearchConfigOptions, useSearchConfig} from './useSearchConfig'
import {useWithinField} from './useWithinField'
import {
  SearchConfigWithSparseFields,
  SparseField,
} from 'quickstart/lib/search/config'

const log = logger('useGlobalSearchConfig')

const WITHIN_PARAM_NAME = 'within'

const PREFIX = 'searchParam-'

export const defaultTips = `
**Required terms**
Use a plus sign before each required term.

_Example_
+automobile +windshield +glass

**Excluded terms**
Use a minus sign before each excluded term.

_Example_
+rotary +kiln –cement

**Wildcards**
Use an asterisk anywhere after the first letter in the term.

_Example_
auto* will return results for automobile, automatic, autonomous

**Exact phrases**
Enclose in double quotes.

_Example_
"basic refractories"
`.trim()

export const useGlobalSearchConfig = (
  config: SearchBlockGlobalConfig,
  options?: UseSearchConfigOptions,
) => {
  const customSearchConfig = useMemo(
    () =>
      tryJson<any>(config.customSearchConfigJson, e =>
        log.error('failed to parse customSearchConfigJson', e),
      ),
    [config.customSearchConfigJson],
  )

  const within = useWithinField({
    config: config.fields.within,
    name: WITHIN_PARAM_NAME,
    urlPrefix: PREFIX,
  })

  const baseConfig: Partial<SearchConfigWithSparseFields> = {
    metaTypes: {
      metadata: config.metadataMetaTypes,
    },
    depths: (
      [
        !!config.metadataMetaTypes.length && 'metadata',
        !!config.tocDepth && 'toc',
        !!config.fulltextDepth && 'fulltext',
      ] as const
    ).filter(truthy),
    fields: {
      terms: {
        tips: config.fields.terms.customTips || defaultTips,
      },
      // ALWAYS include within, even if not enabled as a facet. That way the
      // Search Within block works, and the user can still remove the
      // constraint with the clickable filter tag. If not enabled as a facet,
      // it will be excluded from order below.
      [WITHIN_PARAM_NAME]: within as SparseField,
    },
    order: ['terms', 'within'],
  }

  const searchConfig = useSearchConfig(
    customSearchConfig ? deepMerge(baseConfig)(customSearchConfig) : baseConfig,
    options,
  )

  return searchConfig
}
